import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { Alert, Button, Divider, FormControl, InputLabel, Select, Stack, Typography } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import DialogButtons from "@entrilia/shared/components/DialogButtons.tsx";
import { MenuItemStyled } from "@entrilia/shared/components/MenuItemStyled.tsx";
import DialogHeader from "@entrilia/shared/components/dialog/DialogHeader.tsx";
import PromptDialog from "@entrilia/shared/components/dialog/PromptDialog.tsx";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext.ts";
import DeleteDialogIcon from "@entrilia/shared/icons/DeleteDialogIcon.tsx";
import { defined } from "@entrilia/shared/utilities/typeHelper.ts";
import type { ReportGroup } from "../../../../../../api/types/svTypes.ts";
import useClientContext from "../../../../../../context/useClientContext.ts";
import type { GroupType } from "../GroupType.ts";
import { useGroupsContext } from "../contexts/GroupsContext.ts";
import AddGroupDialog from "./AddGroupDialog.tsx";

interface Props {
  group: ReportGroup;
  type: GroupType;
  onDeleted: () => void;
  onClose: () => void;
}

export const DeleteGroupDialog = (props: Props) => {
  const { group, onDeleted, onClose, type } = props;
  const { clientCode } = useClientContext();
  const { groups: reportGroups, actions: groupActions } = useGroupsContext();

  const { sendNotification } = useNotificationContext();

  const groups = defined(reportGroups[clientCode]);
  const initialReassignGroupId = groups.find((rg) => rg.id !== group.id)?.id ?? "";

  const [newGroupId, setNewGroupId] = useState(initialReassignGroupId);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const [levelSelectionRequired, setLevelSelectionRequired] = useState(false);
  const [showAddLevel, setShowAddLevel] = useState(false);

  const filteredGroups = useMemo(() => {
    return groups.filter((rg) => rg.id !== group.id);
  }, [group.id, groups]);
  const validReassignmentTarget =
    newGroupId !== "" && newGroupId !== group.id && filteredGroups.some((rg) => rg.id === newGroupId);

  const handleDelete = useCallback(
    // eslint-disable-next-line @eslint-react/immutability -- groupActions is a service object; calling its methods mutates nothing React owns
    async (reassignToGroupId?: string) => {
      setError(undefined);
      setSaving(true);
      try {
        // eslint-disable-next-line @eslint-react/immutability -- see above
        const result = await groupActions.delete(clientCode, group.id, reassignToGroupId);

        if (result.status === "ReportsAttached") {
          setLevelSelectionRequired(true);
          setSaving(false);
          return;
        }

        if (result.reportCount > 0) {
          await groupActions.reloadReports?.();
        }

        onDeleted();
        onClose();
      } catch {
        setError("An error occurred while deleting the group.");
        setSaving(false);
      }
    },
    [groupActions, clientCode, group.id, onDeleted, onClose]
  );

  const onSave = useCallback(() => {
    if (levelSelectionRequired) {
      if (!validReassignmentTarget) {
        setError("An error occurred while deleting the group.");
        return;
      }
      handleDelete(newGroupId);
    } else {
      handleDelete();
    }
  }, [levelSelectionRequired, newGroupId, handleDelete, validReassignmentTarget]);

  return (
    <PromptDialog open={true} sx={{ width: 510 }}>
      <DialogHeader text="Delete Group" disabled={saving} onClose={() => onClose()} />
      {error && (
        <Alert sx={{ mb: ".5rem" }} severity="error">
          {error}
        </Alert>
      )}
      <Stack
        spacing={2}
        useFlexGap
        sx={{
          px: 2.5,
          py: 1,
          flex: 1,
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
        }}
      >
        <DeleteDialogIcon sx={{ width: 80, height: 80 }} />
        <Typography sx={{ color: "text.secondary" }}>Are you sure you want to delete the group?</Typography>
        <Typography variant="h6" sx={{ pb: 2, color: "text.primary" }}>
          {group.caption}
        </Typography>
        {levelSelectionRequired && (
          <>
            <Typography color="textPrimary" sx={{ pb: 2 }}>
              {`There are ${type}s attached to this Group, select new Group for those ${type}s`}:
            </Typography>
            <FormControl fullWidth disabled={saving}>
              <InputLabel>Group</InputLabel>
              <Select
                itemID="id"
                value={newGroupId}
                title={"caption"}
                label="Group"
                onChange={(evt) => {
                  setNewGroupId(evt.target.value);
                  setError(undefined);
                }}
              >
                {filteredGroups.map((rg) => {
                  return (
                    <MenuItemStyled key={rg.id} value={rg.id}>
                      <Typography>{rg.caption}</Typography>
                    </MenuItemStyled>
                  );
                })}
                <Divider />
                <MenuItemStyled sx={{ gap: 1 }} onClick={() => setShowAddLevel(true)}>
                  <AddRoundedIcon color="primary" />
                  <Typography color="primary">New Group</Typography>
                </MenuItemStyled>
              </Select>
            </FormControl>
          </>
        )}
      </Stack>
      {showAddLevel && (
        <AddGroupDialog
          clientCode={clientCode}
          onAdded={(group) => {
            setNewGroupId(group.id);
            sendNotification("The group was successfully created.");
          }}
          onClose={() => setShowAddLevel(false)}
        />
      )}
      <DialogButtons onCancel={onClose} cancelDisabled={saving}>
        <Button
          variant="contained"
          disabled={saving || (levelSelectionRequired && !validReassignmentTarget)}
          loading={saving}
          onClick={onSave}
        >
          Delete
        </Button>
      </DialogButtons>
    </PromptDialog>
  );
};

export default DeleteGroupDialog;
